import { faApplePay } from '@fortawesome/free-brands-svg-icons/faApplePay';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ACTION_STATUSES } from 'shared/consts';
import { APPLE_PAY_PAYMENT_TYPE_ID } from 'shared/consts/payment-method-types';
import { selectCurrentPaymentMethodId } from 'store/payment/selectors';
import { ButtonContainer, StyledButton, StyledNativePaymentIcon } from '../styles';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface ButtonProps {
  isPaymentMethodsAvailable?: boolean | number;
  isFreeCart?: boolean;
  submitCartStatus: string;
  onCTAClick: VoidFunction;
  pendingNativeMethod?: boolean;
  className?: string;
  checkoutDisabled?: boolean;
  autoConfirm?: boolean;
}

export const Button = ({
  isPaymentMethodsAvailable,
  isFreeCart,
  submitCartStatus,
  onCTAClick,
  pendingNativeMethod,
  className,
  checkoutDisabled,
  autoConfirm,
}: ButtonProps) => {
  const intl = useIntl();
  const { enableHqoMiniappApplePay } = useFlags();

  const paymentMethodId = useSelector(selectCurrentPaymentMethodId);
  const isApplePay = paymentMethodId === APPLE_PAY_PAYMENT_TYPE_ID && enableHqoMiniappApplePay;

  const buttonDisabled = useMemo(
    () => checkoutDisabled || submitCartStatus === ACTION_STATUSES.FULFILLED,
    [checkoutDisabled, submitCartStatus],
  );

  const loading = useMemo(
    () => submitCartStatus === ACTION_STATUSES.PENDING || pendingNativeMethod,
    [submitCartStatus, pendingNativeMethod],
  );

  const checkoutButtonText = useMemo(() => {
    if (!isPaymentMethodsAvailable && !isFreeCart) {
      return 'add_payment_method';
    }

    if (isApplePay && !isFreeCart) {
      return 'check_out_with';
    }

    if (submitCartStatus === ACTION_STATUSES.FULFILLED) {
      return 'booking_success_notification_title';
    }

    if (autoConfirm === false) {
      return 'request';
    }

    return 'book';
  }, [isPaymentMethodsAvailable, isFreeCart, isApplePay, submitCartStatus, autoConfirm]);

  return (
    <ButtonContainer className={className}>
      <StyledButton
        data-testid="checkout-button"
        disabled={buttonDisabled || loading}
        loading={loading}
        onClick={onCTAClick}
        text={intl.formatMessage({ id: checkoutButtonText })}
        Icon={isApplePay && !isFreeCart && <StyledNativePaymentIcon icon={faApplePay} size="2x" />}
        isNativePayment={isApplePay && !isFreeCart}
        variant="primary"
      />
    </ButtonContainer>
  );
};
